<template>
  <div class="single-news-view">
    <template v-if="news">
      <TextHeroComponent :data="newsHeroData" />
      <SpacerComponent :data="negativeSpacerData" />
      <SpacerComponent :data="positiveSpacerData" />
    </template>

    <section class="single-news-page">
      <div class="single-news-inner">
        <router-link
          class="single-news-back"
          to="/media#media-filters">
          <svg
            aria-hidden="true"
            viewBox="0 0 20 20">
            <path d="m12.5 5-5 5 5 5" />
          </svg>
          Back to Media
        </router-link>

        <div
          v-if="isLoading"
          class="single-news-status"
          aria-live="polite">
          Loading…
        </div>

        <div
          v-else-if="errorMessage"
          class="single-news-status"
          role="alert">
          <p>{{ errorMessage }}</p>
          <button
            type="button"
            @click="loadNews">
            Try Again
          </button>
        </div>

        <article
          v-else-if="news"
          class="single-news-article">
          <header class="single-news-header">
            <time :datetime="news.dateUTC">
              {{ formatDate(news.dateUTC) }}
            </time>

            <h1>{{ news.title }}</h1>

            <span
              class="single-news-accent"
              aria-hidden="true"></span>
          </header>

          <div
            class="single-news-body"
            v-html="news.body"></div>
        </article>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/main'
import SpacerComponent from '@/components/modules/SpacerComponent.vue'
import TextHeroComponent from '@/components/modules/TextHeroComponent.vue'

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
})

const router = useRouter()
const store = useStore()
const news = ref(null)
const isLoading = ref(true)
const errorMessage = ref('')
let requestId = 0

const newsCategoryLabels = {
  'General Releases': 'Press Releases',
  Statements: 'Company Statements',
}

const newsCategoryLabel = computed(() => {
  const categories = news.value?.categories || []
  const category = categories.find((item) => newsCategoryLabels[item])

  return category ? newsCategoryLabels[category] : categories[0] || ''
})

const newsHeroData = computed(() => ({
  hero_text: newsCategoryLabel.value,
  use_white_version: false,
  add_hero_subtitle: false,
  hero_subtitle: '',
}))

const negativeSpacerData = {
  separator: false,
  rounded: 'none',
  spacer_height: -100,
  spacer_height_tablet: -60,
  spacer_height_mobile: -50,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: 'rgba(255, 255, 255, 0)',
}

const positiveSpacerData = {
  separator: false,
  rounded: 'tl',
  spacer_height: 100,
  spacer_height_tablet: 60,
  spacer_height_mobile: 50,
  back_background_color: 'rgba(255, 255, 255, 0)',
  background_color: '#ffffff',
}

const dateValue = (value) => {
  if (!value) return null

  const normalized = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value)
    ? value
    : `${value}Z`
  const date = new Date(normalized)

  return Number.isNaN(date.getTime()) ? null : date
}

const formatDate = (value) => {
  const date = dateValue(value)
  if (!date) return ''

  return new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(date)
}

const loadNews = async () => {
  const activeRequest = ++requestId
  isLoading.value = true
  errorMessage.value = ''
  news.value = null

  try {
    const siteUrl = String(store.site.url || '').replace(/\/$/, '')
    const endpoint = `${siteUrl}/wp-json/revmed/v1/news/${encodeURIComponent(
      props.id
    )}`
    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
    })

    if (response.status === 404) {
      router.replace('/404')
      return
    }

    if (!response.ok) {
      throw new Error(`News detail request failed: ${response.status}`)
    }

    const payload = await response.json()

    if (!payload?.data?.body) {
      throw new Error(
        'The news detail response did not include article content.'
      )
    }

    if (activeRequest !== requestId) return

    news.value = payload.data
    store.updateDocTitle({
      parts: [news.value.title, store.site.name],
    })
  } catch (error) {
    if (activeRequest !== requestId) return

    console.error('Unable to load the GCS news article.', error)
    errorMessage.value =
      'This news article is temporarily unavailable. Please try again.'
  } finally {
    if (activeRequest === requestId) {
      isLoading.value = false
    }
  }
}

watch(() => props.id, loadNews, { immediate: true })
</script>

<style lang="scss" scoped>
.single-news-page {
  @apply min-h-screen bg-white pb-16 pt-10 lg:pb-[100px] lg:pt-[60px];

  .single-news-inner {
    @apply inner-container;

    .single-news-back {
      @apply mb-8 inline-flex items-center gap-2 rounded-full bg-balanced px-6 py-3 font-FKGrotesk text-[16px] text-white no-underline transition-opacity hover:bg-humble;

      svg {
        @apply h-5 w-5;

        path {
          fill: none;
          stroke: white;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-width: 1.5;
        }
      }
    }

    .single-news-status,
    .single-news-article {
      @apply rounded-[20px] bg-white/70 py-10 lg:py-[70px];
    }

    .single-news-status {
      @apply font-FKGrotesk text-[18px] text-humble;

      button {
        @apply mt-5 rounded-full bg-progressive px-6 py-3 text-[16px] text-humble transition-colors hover:bg-balanced hover:text-white;
      }
    }

    .single-news-article {
      .single-news-header {
        @apply mb-10 max-w-[1000px] lg:mb-[55px];

        time {
          @apply block font-FKGrotesk text-[14px] leading-tight text-gray_green;
        }

        h1 {
          @apply mt-5 font-FKDisplay text-[28px] font-normal leading-[1.05] tracking-[-0.02em] text-balanced md:text-[40px];
        }

        .single-news-accent {
          @apply mt-8 block h-[3px] w-[200px] max-w-full;
          background: linear-gradient(
            90deg,
            #70ffe5 0%,
            #70ffe5 50%,
            #13d377 50%,
            #13d377 100%
          );
        }
      }

      .single-news-body:deep() {
        @apply max-w-[920px] overflow-x-auto font-FKGrotesk text-[17px] leading-[1.55] text-humble;

        > :first-child {
          @apply mt-0;
        }

        > :last-child {
          @apply mb-0;
        }

        p {
          @apply my-5;
          text-align: left;
        }

        h2,
        h3,
        h4,
        h5,
        h6 {
          @apply mb-4 mt-10 font-FKDisplay font-normal leading-tight text-balanced;
        }

        h2 {
          @apply text-[34px] lg:text-[42px];
        }

        h3 {
          @apply text-[28px] lg:text-[34px];
        }

        h4,
        h5,
        h6 {
          @apply text-[22px] lg:text-[26px];
        }

        a {
          @apply text-balanced underline decoration-balanced underline-offset-2 transition-opacity hover:opacity-70;
        }

        ul,
        ol {
          @apply my-5 pl-6;
        }

        ul {
          @apply list-disc;
        }

        ol {
          @apply list-decimal;
        }

        li {
          @apply mb-2;
        }

        blockquote {
          @apply my-8 border-l-4 border-progressive pl-6 text-[20px];
        }

        img {
          @apply my-8 h-auto max-w-full rounded-[10px];
        }

        iframe {
          @apply my-8 block w-full max-w-full rounded-[10px] border-0 bg-functional;
          aspect-ratio: 16 / 9;
          height: auto !important;
          min-height: min(520px, 56.25vw);
        }

        table {
          @apply my-8 min-w-[650px] border-collapse;
        }

        th,
        td {
          @apply border border-gray-300 p-3 text-left align-top;
        }

        sup {
          @apply text-[0.7em];
        }
      }
    }
  }
}
</style>
